"use client"

import type React from "react"
import { useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
  Card,
  CardBody,
} from "reactstrap"
import { FaUpload, FaCheckCircle, FaFileAlt, FaReceipt } from "react-icons/fa"
import { BadgeRequestService } from "../../../Service/BadgeRequestService"
import type { PrintOrder } from "./badge-request"
import { toast } from "react-toastify"

interface Props {
  isOpen: boolean
  toggle: () => void
  order: PrintOrder
  onSuccess: () => void
}

const PaymentModal: React.FC<Props> = ({ isOpen, toggle, order, onSuccess }) => {
  const [transactionReference, setTransactionReference] = useState("")
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)

  // Calculate total amount from order details
  const calculateTotalAmount = (order: PrintOrder): number => {
    if (!order.order_details) return 0
    return order.order_details.reduce((total, detail) => {
      return total + Number.parseFloat(detail.amount)
    }, 0)
  }

  const totalAmount = calculateTotalAmount(order)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"]
      const maxSize = 10 * 1024 * 1024 // 10MB

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a valid image (JPEG, PNG) or PDF file")
        return
      }

      if (file.size > maxSize) {
        toast.error("File size must be less than 10MB")
        return
      }

      setProofFile(file)
    }
  }

  const handleSubmitPayment = async () => {
    if (!proofFile) {
      toast.error("Please upload proof of payment")
      return
    }

    try {
      setLoading(true)

      const formData = new FormData()
      formData.append("payment_method", "payslip")
      formData.append("amount", totalAmount.toString())
      formData.append("proof_of_payment", proofFile)

      if (transactionReference.trim()) {
        formData.append("transaction_number", transactionReference.trim())
      }

      await BadgeRequestService.submitPayment(order.id, formData)
      // toast.success("Payment proof submitted successfully!")
      onSuccess()
    } catch (error) {
      console.error("Payment submission failed:", error)
      toast.error("Failed to submit payment proof")
    } finally {
      setLoading(false)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const triggerFileInput = () => {
    document.getElementById("proof-upload")?.click()
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="md" centered className="compact-modal">
        <div className="modal-content border-0 shadow">
          <ModalHeader toggle={toggle} className="border-0 py-3 px-3">
            <div className="d-flex align-items-center">
              <div className="bg-primary rounded-circle p-2 me-2">
                <FaReceipt className="text-white" size={16} />
              </div>
              <div>
                <h5 className="mb-0 fw-bold">Payment Verification</h5>
                <small className="text-muted">Order #{order.order_number}</small>
              </div>
            </div>
          </ModalHeader>

          <ModalBody className="px-3 py-2">
            {/* Compact Order Summary */}
            <Card className="mb-3 border-0 bg-light">
              <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted fw-semibold">TOTAL AMOUNT</small>
                    <h4 className="mb-0 fw-bold text-primary">{totalAmount.toLocaleString()} RWF</h4>
                  </div>
                  <div className="text-muted">
                    <small>Payment Required</small>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Compact Instructions */}
            <div className="mb-3 p-3 bg-warning bg-opacity-10 rounded">
              <div className="d-flex align-items-start">
                <FaFileAlt className="text-info me-2 mt-1" size={14} />
                <div>
                  <h6 className="mb-1 fw-semibold">Quick Steps</h6>
                  <small className="text-muted">
                    1. Make payment • 2. Upload receipt • 3. Add reference (optional)
                  </small>
                </div>
              </div>
            </div>

            {/* Compact File Upload */}
            <Card className="mb-3 border-0 shadow-sm">
              <CardBody className="p-3">
                <div className="mb-2">
                  <Label className="fw-semibold mb-2">
                    Upload Payment Proof <span className="text-danger">*</span>
                  </Label>
                  <small className="text-muted d-block">Max 10MB • JPEG, PNG, PDF</small>
                </div>

                {!proofFile ? (
                  <div
                    className="upload-area border-2 border-dashed rounded p-3 text-center"
                    style={{
                      borderColor: "#dee2e6",
                      backgroundColor: "#f8f9fa",
                      cursor: "pointer",
                    }}
                    onClick={triggerFileInput}
                  >
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      className="d-none"
                      id="proof-upload"
                    />
                    <FaUpload className="text-primary mb-2" size={20} />
                    <div>
                      <small className="fw-semibold d-block">Drop file here or click to browse</small>
                      <Button color="primary" size="sm" className="mt-2" onClick={triggerFileInput}>
                        Choose File
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-success bg-opacity-10 border border-success border-opacity-25 rounded p-3">
                    <div className="d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2" size={16} />
                      <div className="flex-grow-1">
                        <small className="fw-semibold text-success d-block">{proofFile.name}</small>
                        <small className="text-muted">{formatFileSize(proofFile.size)}</small>
                      </div>
                      <Button color="outline-secondary" size="sm" onClick={() => setProofFile(null)}>
                        Change
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Compact Transaction Reference */}
            <FormGroup className="mb-0">
              <Label className="fw-semibold mb-2">
                Transaction Reference <span className="badge bg-light text-muted ms-1">Optional</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter transaction ID or reference"
                value={transactionReference}
                onChange={(e) => setTransactionReference(e.target.value)}
                xs="sm"
              />
              <small className="text-muted">Helps process payment faster</small>
            </FormGroup>
          </ModalBody>

          <ModalFooter className="border-0 px-3 py-3">
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                <small className="text-muted">Total: </small>
                <span className="fw-bold">{totalAmount.toLocaleString()} RWF</span>
              </div>
              <div className="d-flex gap-2">
                <Button color="light" onClick={toggle} disabled={loading} size="sm">
                  Cancel
                </Button>
                <Button color="primary" onClick={handleSubmitPayment} disabled={loading || !proofFile} size="sm">
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-1" size={12} />
                      Submit Proof
                    </>
                  )}
                </Button>
              </div>
            </div>
          </ModalFooter>
        </div>
      </Modal>

      <style>{`
        .compact-modal .modal-content {
          border-radius: 12px !important;
        }
        
        .compact-modal .modal-header {
          background-color: #f8f9fa;
        }
        
        .upload-area:hover {
          border-color: #0d6efd !important;
          background-color: #f0f7ff !important;
        }
        
        .upload-area:hover .text-primary {
          transform: translateY(-2px);
          transition: transform 0.2s ease;
        }
        
        .card {
          transition: all 0.2s ease;
        }
        
        .btn {
          transition: all 0.2s ease;
        }
        
        .btn:hover {
          transform: translateY(-1px);
        }
        
        .form-control:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.1);
        }
      `}</style>
    </>
  )
}

export default PaymentModal
